// Articulated goalkeeper: a jointed rig built from primitives, a pose library blended per frame,
// procedural dive/jump kinematics and sphere colliders that the ball physics collides with.
import * as THREE from 'three';
import { jerseyTexture } from './textures.js';
import { GOAL } from './config.js';

const LINE_Z = 0.3; // keeper's feet sit just in front of the goal line

// Pose library. Angles in radians: [x, y, z]. Conventions (keeper faces +z):
//  - limbs hang down; rotation.x < 0 swings a limb forward, rotation.z swings it sideways (+z -> +x)
//  - spine rotation.x > 0 leans forward
// "N" = the arm/leg on the keeper's -x side, "P" = the +x side.
const POSES = {
  ready: {
    hipsY: 0.84, spine: [0.32, 0, 0], neck: [-0.22, 0, 0],
    shN: [-0.55, 0, -0.62], elN: [-0.95, 0, 0], shP: [-0.55, 0, 0.62], elP: [-0.95, 0, 0],
    thN: [-0.62, 0, -0.2], knN: [1.0, 0, 0], ankN: [-0.38, 0, 0.2],
    thP: [-0.62, 0, 0.2], knP: [1.0, 0, 0], ankP: [-0.38, 0, -0.2],
  },
  set: {
    hipsY: 0.78, spine: [0.42, 0, 0], neck: [-0.32, 0, 0],
    shN: [-0.45, 0, -0.85], elN: [-0.7, 0, 0], shP: [-0.45, 0, 0.85], elP: [-0.7, 0, 0],
    thN: [-0.8, 0, -0.24], knN: [1.3, 0, 0], ankN: [-0.5, 0, 0.24],
    thP: [-0.8, 0, 0.24], knP: [1.3, 0, 0], ankP: [-0.5, 0, -0.24],
  },
  reach: { // fully stretched, arms above the head: used for dives (the root rolls the whole body)
    hipsY: 0.99, spine: [-0.05, 0, 0], neck: [0.05, 0, 0],
    shN: [-0.15, 0, -3.32], elN: [-0.08, 0, 0], shP: [-0.15, 0, 3.32], elP: [-0.08, 0, 0], // hands together above the head
    thN: [0.05, 0, -0.12], knN: [0.15, 0, 0], ankN: [0.3, 0, 0],
    thP: [-0.35, 0, 0.18], knP: [0.7, 0, 0], ankP: [0.3, 0, 0],
  },
  jump: {
    hipsY: 0.97, spine: [-0.08, 0, 0], neck: [-0.15, 0, 0],
    shN: [-0.25, 0, -3.2], elN: [-0.15, 0, 0], shP: [-0.25, 0, 3.2], elP: [-0.15, 0, 0],
    thN: [-0.45, 0, -0.08], knN: [0.75, 0, 0], ankN: [0.4, 0, 0],
    thP: [-0.2, 0, 0.08], knP: [0.4, 0, 0], ankP: [0.4, 0, 0],
  },
  block: { // stay big: arms out and up for shots straight at the keeper
    hipsY: 0.9, spine: [0.15, 0, 0], neck: [-0.1, 0, 0],
    shN: [-0.9, 0, -1.25], elN: [-0.25, 0, 0], shP: [-0.9, 0, 1.25], elP: [-0.25, 0, 0],
    thN: [-0.3, 0, -0.3], knN: [0.45, 0, 0], ankN: [-0.1, 0, 0.3],
    thP: [-0.3, 0, 0.3], knP: [0.45, 0, 0], ankP: [-0.1, 0, -0.3],
  },
  smother: { // low, central: drop and spread
    hipsY: 0.5, spine: [0.75, 0, 0], neck: [-0.55, 0, 0],
    shN: [-1.25, 0, -0.35], elN: [-0.15, 0, 0], shP: [-1.25, 0, 0.35], elP: [-0.15, 0, 0],
    thN: [-1.35, 0, -0.55], knN: [2.0, 0, 0], ankN: [-0.6, 0, 0.5],
    thP: [-1.35, 0, 0.55], knP: [2.0, 0, 0], ankP: [-0.6, 0, -0.5],
  },
  lying: {
    hipsY: 0.99, spine: [0.1, 0, 0], neck: [-0.1, 0, 0],
    shN: [-0.4, 0, -2.6], elN: [-0.5, 0, 0], shP: [-0.4, 0, 2.6], elP: [-0.5, 0, 0],
    thN: [-0.2, 0, -0.1], knN: [0.5, 0, 0], ankN: [0.3, 0, 0],
    thP: [-0.6, 0, 0.15], knP: [1.0, 0, 0], ankP: [0.3, 0, 0],
  },
  celebrate: {
    hipsY: 0.97, spine: [-0.15, 0, 0], neck: [-0.3, 0, 0],
    shN: [-0.3, 0, -2.5], elN: [-1.6, 0, 0], shP: [-0.3, 0, 2.5], elP: [-1.6, 0, 0],
    thN: [-0.1, 0, -0.12], knN: [0.15, 0, 0], ankN: [0, 0, 0.1],
    thP: [-0.1, 0, 0.12], knP: [0.15, 0, 0], ankP: [0, 0, -0.1],
  },
  dejected: {
    hipsY: 0.9, spine: [0.55, 0, 0], neck: [0.45, 0, 0],
    shN: [-0.55, 0, -0.18], elN: [-0.35, 0, 0], shP: [-0.55, 0, 0.18], elP: [-0.35, 0, 0],
    thN: [-0.35, 0, -0.1], knN: [0.55, 0, 0], ankN: [-0.2, 0, 0.1],
    thP: [-0.35, 0, 0.1], knP: [0.55, 0, 0], ankP: [-0.2, 0, -0.1],
  },
};
const JOINTS = ['spine', 'neck', 'shN', 'elN', 'shP', 'elP', 'thN', 'knN', 'ankN', 'thP', 'knP', 'ankP'];

const ease = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
const easeOut = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : 1 - (1 - t) ** 3);
const lerp = (a, b, t) => a + (b - a) * t;

export class Keeper {
  constructor(scene) {
    this.scene = scene;
    this.root = new THREE.Group();
    this.materials = {};
    this.disposables = [];
    this.joints = {};
    this.colliders = [];
    this.world = []; // { pos, r, kind } in world space, refreshed by updateColliders()
    this.build();
    scene.add(this.root);
    this.pose = this.blankPose();
    this.measureReach();
    this.reset(0);
  }

  blankPose() {
    const p = { hipsY: 0.99 };
    for (const j of JOINTS) p[j] = [0, 0, 0];
    return p;
  }

  mat(name, params) {
    const m = new THREE.MeshStandardMaterial(params);
    this.materials[name] = m;
    this.disposables.push(m);
    return m;
  }

  geo(g) { this.disposables.push(g); return g; }

  build() {
    const skin = this.mat('skin', { color: '#8d5524', roughness: 0.7 });
    const shirt = this.mat('shirt', { color: '#ffffff', roughness: 0.75 });
    const sleeve = this.mat('sleeve', { color: '#ff7a1a', roughness: 0.75 });
    const shorts = this.mat('shorts', { color: '#1b1b1f', roughness: 0.8 });
    const socks = this.mat('socks', { color: '#ff7a1a', roughness: 0.8 });
    const glove = this.mat('glove', { color: '#f4f4f4', roughness: 0.55 });
    const strap = this.mat('strap', { color: '#ff7a1a', roughness: 0.5 });
    const boot = this.mat('boot', { color: '#111318', roughness: 0.35, metalness: 0.2 });
    const sole = this.mat('sole', { color: '#d8fd71', roughness: 0.6 });
    const hair = this.mat('hair', { color: '#17120f', roughness: 0.9 });
    const dark = this.mat('dark', { color: '#0d0d0d', roughness: 0.4 });

    const capsule = (r, len, m, y, sx = 1, sz = 1) => {
      const mesh = new THREE.Mesh(this.geo(new THREE.CapsuleGeometry(r, len, 6, 12)), m);
      mesh.position.y = y;
      mesh.scale.set(sx, 1, sz);
      mesh.castShadow = true;
      return mesh;
    };
    const group = (parent, name, x, y, z = 0) => {
      const g = new THREE.Group();
      g.position.set(x, y, z);
      parent.add(g);
      if (name) this.joints[name] = g;
      return g;
    };
    const collider = (parent, kind, r, x = 0, y = 0, z = 0) => {
      const o = new THREE.Object3D();
      o.position.set(x, y, z);
      parent.add(o);
      this.colliders.push({ obj: o, r, kind });
    };

    const hips = group(this.root, 'hips', 0, 0.99);
    const pelvis = new THREE.Mesh(this.geo(new THREE.SphereGeometry(0.17, 16, 12)), shorts);
    pelvis.scale.set(1.15, 0.85, 0.8);
    pelvis.castShadow = true;
    hips.add(pelvis);
    collider(hips, 'body', 0.17, 0, 0, 0);

    const spine = group(hips, 'spine', 0, 0.06);
    const torso = capsule(0.165, 0.3, shirt, 0.27, 1.2, 0.72);
    torso.rotation.y = Math.PI; // capsule UV seam at the back so the chest number faces the shooter
    spine.add(torso);
    collider(spine, 'body', 0.19, 0, 0.14, 0);
    collider(spine, 'body', 0.2, 0, 0.4, 0);

    const neck = group(spine, 'neck', 0, 0.6);
    const neckMesh = capsule(0.05, 0.06, skin, 0.03);
    neck.add(neckMesh);
    const head = new THREE.Group();
    head.position.y = 0.15;
    neck.add(head);
    const skull = new THREE.Mesh(this.geo(new THREE.SphereGeometry(0.115, 20, 16)), skin);
    skull.scale.set(0.92, 1.05, 0.98);
    skull.castShadow = true;
    head.add(skull);
    const hairMesh = new THREE.Mesh(this.geo(new THREE.SphereGeometry(0.122, 20, 12, 0, Math.PI * 2, 0, Math.PI * 0.55)), hair);
    hairMesh.position.set(0, 0.012, -0.012);
    hairMesh.rotation.x = -0.25;
    head.add(hairMesh);
    for (const s of [-1, 1]) {
      const eye = new THREE.Mesh(this.geo(new THREE.SphereGeometry(0.016, 8, 6)), dark);
      eye.position.set(s * 0.04, 0.02, 0.102);
      head.add(eye);
      const brow = new THREE.Mesh(this.geo(new THREE.BoxGeometry(0.04, 0.008, 0.01)), hair);
      brow.position.set(s * 0.04, 0.048, 0.104);
      head.add(brow);
      const ear = new THREE.Mesh(this.geo(new THREE.SphereGeometry(0.025, 8, 6)), skin);
      ear.position.set(s * 0.108, 0.0, 0);
      ear.scale.set(0.5, 1, 0.8);
      head.add(ear);
    }
    const nose = new THREE.Mesh(this.geo(new THREE.ConeGeometry(0.016, 0.04, 6)), skin);
    nose.rotation.x = Math.PI / 2;
    nose.position.set(0, 0, 0.118);
    head.add(nose);
    collider(head, 'body', 0.13, 0, 0, 0);

    for (const [s, n] of [[-1, 'N'], [1, 'P']]) {
      const sh = group(spine, 'sh' + n, s * 0.23, 0.5);
      const shoulderBall = new THREE.Mesh(this.geo(new THREE.SphereGeometry(0.075, 12, 10)), sleeve);
      sh.add(shoulderBall);
      sh.add(capsule(0.058, 0.2, sleeve, -0.14));
      collider(sh, 'arm', 0.075, 0, -0.15, 0);
      const el = group(sh, 'el' + n, 0, -0.29);
      el.add(capsule(0.05, 0.18, sleeve, -0.12));
      collider(el, 'arm', 0.07, 0, -0.13, 0);
      const wr = group(el, 'wr' + n, 0, -0.26);
      const gloveMesh = capsule(0.065, 0.09, glove, -0.08, 1.15, 0.62);
      wr.add(gloveMesh);
      const thumb = capsule(0.024, 0.05, glove, -0.05);
      thumb.position.set(-s * 0.05, -0.05, 0.035);
      thumb.rotation.z = -s * 0.5;
      wr.add(thumb);
      const cuff = new THREE.Mesh(this.geo(new THREE.CylinderGeometry(0.06, 0.06, 0.05, 12)), strap);
      cuff.position.y = -0.005;
      wr.add(cuff);
      collider(wr, 'glove', 0.13, 0, -0.09, 0);

      const th = group(hips, 'th' + n, s * 0.1, -0.05);
      th.add(capsule(0.088, 0.12, shorts, -0.1));
      th.add(capsule(0.07, 0.22, skin, -0.29));
      collider(th, 'leg', 0.095, 0, -0.22, 0);
      const kn = group(th, 'kn' + n, 0, -0.45);
      kn.add(capsule(0.062, 0.3, socks, -0.2));
      collider(kn, 'leg', 0.075, 0, -0.2, 0);
      const ank = group(kn, 'ank' + n, 0, -0.42);
      const bootMesh = new THREE.Mesh(this.geo(new THREE.BoxGeometry(0.1, 0.075, 0.27)), boot);
      bootMesh.position.set(0, -0.03, 0.05);
      bootMesh.castShadow = true;
      ank.add(bootMesh);
      const soleMesh = new THREE.Mesh(this.geo(new THREE.BoxGeometry(0.102, 0.018, 0.272)), sole);
      soleMesh.position.set(0, -0.07, 0.05);
      ank.add(soleMesh);
      collider(ank, 'leg', 0.075, 0, -0.03, 0.05);
    }
    this.root.traverse((o) => { if (o.isMesh) o.castShadow = true; });
  }

  /** Measure, from the actual rig, how far the glove centre is from the feet when fully stretched. */
  measureReach() {
    this.applyPose(POSES.reach);
    this.root.position.set(0, 0, 0);
    this.root.rotation.set(0, 0, 0);
    this.root.updateMatrixWorld(true);
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    this.joints.wrN.localToWorld(a.set(0, -0.09, 0));
    this.joints.wrP.localToWorld(b.set(0, -0.09, 0));
    this.reach = (a.y + b.y) / 2; // ~2.2 m
  }

  setKit(kit, skinColor) {
    const m = this.materials;
    if (m.shirt.map) m.shirt.map.dispose();
    m.shirt.map = jerseyTexture(kit.shirt, kit.trim);
    m.shirt.needsUpdate = true;
    m.sleeve.color.set(kit.shirt);
    m.shorts.color.set(kit.shorts);
    m.socks.color.set(kit.socks);
    m.glove.color.set(kit.gloves);
    m.strap.color.set(kit.strap);
    if (skinColor) m.skin.color.set(skinColor);
  }

  applyPose(p) {
    this.joints.hips.position.y = p.hipsY;
    for (const j of JOINTS) {
      const r = p[j];
      this.joints[j].rotation.set(r[0], r[1], r[2]);
    }
  }

  /** Blend this.pose toward target pose by factor k (0..1). */
  blendPose(target, k) {
    const p = this.pose;
    p.hipsY = lerp(p.hipsY, target.hipsY, k);
    for (const j of JOINTS) {
      const a = p[j];
      const b = target[j];
      a[0] = lerp(a[0], b[0], k); a[1] = lerp(a[1], b[1], k); a[2] = lerp(a[2], b[2], k);
    }
  }

  /** Back to the ready position on the line, centred at x. */
  reset(x = 0) {
    this.mode = 'ready';
    this.t = 0;
    this.baseX = x;
    this.offsetX = 0; // anticipation lean
    this.root.position.set(x, 0, LINE_Z);
    this.root.rotation.set(0, 0, 0);
    const r = POSES.ready;
    this.pose.hipsY = r.hipsY;
    for (const j of JOINTS) this.pose[j] = [...r[j]];
    this.applyPose(this.pose);
    this.dive = null;
    this.after = null;
    this.updateColliders();
  }

  /** Pre-shot shuffle and anticipation offset (m, toward the side the keeper expects). */
  setAnticipation(offsetX) { this.offsetX = offsetX; }

  getSet() { if (this.mode === 'ready') this.mode = 'set'; }

  /**
   * Commit to a save attempt.
   * target = { x, y }: where the keeper believes the ball will cross the line
   * timeToBall: seconds until the ball reaches the keeper's plane
   * skill: from keeperSkill()
   */
  react(target, timeToBall, skill) {
    const x0 = this.root.position.x;
    const dx = target.x - x0;
    const ty = Math.max(0.12, Math.min(GOAL.height + 0.3, target.y));
    let type = 'dive';
    if (Math.abs(dx) < 0.5 && ty > 1.75) type = 'jump';
    else if (Math.abs(dx) < 0.5 && ty < 0.7) type = 'smother';
    else if (Math.abs(dx) < 0.55) type = 'block';
    const handStart = new THREE.Vector2(x0 + Math.sign(dx) * 0.35, 1.15);
    const travel = handStart.distanceTo(new THREE.Vector2(target.x, ty));
    const duration = Math.max(0.2, Math.min(0.95, travel / skill.diveSpeed + 0.08));
    // time the move to arrive with the ball; never start later than now
    const delay = Math.max(0, timeToBall - duration - 0.03);
    this.mode = type;
    this.t = 0;
    this.dive = {
      type, x0, side: Math.sign(dx) || 1, tx: target.x, ty, duration, delay, launchMax: skill.launch,
      startHipsY: this.pose.hipsY,
    };
  }

  /** In-flight correction toward the real crossing point (limited by skill.adjust). */
  track(target, dt, skill) {
    if (!this.dive || this.mode === 'fall' || this.mode === 'down') return;
    const k = Math.min(1, skill.adjust * dt);
    this.dive.tx += (target.x - this.dive.tx) * k;
    this.dive.ty += (Math.max(0.12, target.y) - this.dive.ty) * k;
  }

  celebrate() { this.mode = this.isDown() ? 'down' : 'celebrate'; this.after = 'celebrate'; }
  dejected() { this.mode = this.isDown() ? 'down' : 'dejected'; this.after = 'dejected'; }
  isDown() { return this.mode === 'dive' || this.mode === 'fall' || this.mode === 'down'; }

  update(dt, time) {
    this.t += dt;
    const root = this.root;
    const d = this.dive;
    switch (this.mode) {
      case 'ready':
      case 'set': {
        const target = this.mode === 'set' ? POSES.set : POSES.ready;
        this.blendPose(target, Math.min(1, dt * 8));
        // bounce on the toes and shuffle along the line
        const shuffle = Math.sin(time * 1.7) * (this.shuffle ?? 0);
        const goalX = this.baseX + shuffle + this.offsetX;
        root.position.x += (goalX - root.position.x) * Math.min(1, dt * 4);
        root.position.y = Math.abs(Math.sin(time * 5.5)) * (this.mode === 'set' ? 0.03 : 0.015);
        root.rotation.z = 0;
        break;
      }
      case 'dive': {
        const t = Math.max(0, this.t - d.delay);
        const p = Math.min(1, t / d.duration);
        const e = easeOut(p);
        const footX = d.x0 + d.side * 0.3;
        const ddx = d.tx - footX;
        const dy = Math.max(0.12, d.ty);
        const dist = Math.hypot(ddx, dy);
        const phi = Math.atan2(ddx, dy); // 0 = straight up, +-pi/2 = horizontal
        const launch = Math.min(d.launchMax, Math.max(0, dist - this.reach));
        root.position.x = lerp(d.x0, footX, ease(Math.min(1, p * 1.6))) + Math.sin(phi) * launch * e;
        root.position.y = Math.cos(phi) * launch * e + Math.sin(p * Math.PI) * 0.08;
        root.rotation.z = -phi * e;
        // load the legs first, then stretch out
        if (t <= 0) this.blendPose(POSES.set, Math.min(1, dt * 10));
        else this.blendPose(POSES.reach, Math.min(1, dt * 14 * (0.4 + p)));
        if (p >= 1 && t > d.duration + 0.08) { this.mode = 'fall'; this.t = 0; d.fallFrom = { x: root.position.x, y: root.position.y, rz: root.rotation.z }; }
        break;
      }
      case 'fall': {
        const p = Math.min(1, this.t / 0.5);
        const f = d.fallFrom;
        const lateral = Math.abs(f.rz) > 0.55;
        if (lateral) {
          const restRz = -d.side * Math.PI * 0.48;
          root.rotation.z = lerp(f.rz, restRz, ease(p));
          root.position.y = lerp(f.y, 0.2, p * p);
          root.position.x = f.x + d.side * 0.25 * ease(p);
          this.blendPose(POSES.lying, Math.min(1, dt * 6));
        } else {
          root.rotation.z = lerp(f.rz, 0, ease(p));
          root.position.y = lerp(f.y, 0, p * p);
          this.blendPose(POSES.ready, Math.min(1, dt * 6));
        }
        if (p >= 1) { this.mode = 'down'; this.t = 0; this.lateralRest = lateral; }
        break;
      }
      case 'down': {
        if (this.after && this.t > 0.55) {
          // get back up before celebrating / sulking
          const p = Math.min(1, (this.t - 0.55) / 0.6);
          root.rotation.z *= 1 - Math.min(1, dt * 7);
          root.position.y *= 1 - Math.min(1, dt * 7);
          this.blendPose(POSES.ready, Math.min(1, dt * 7));
          if (p >= 1) { this.mode = this.after; this.t = 0; root.rotation.z = 0; root.position.y = 0; }
        }
        break;
      }
      case 'jump': {
        const t = Math.max(0, this.t - d.delay);
        const p = Math.min(1, t / Math.max(0.25, d.duration));
        const rise = Math.max(0, Math.min(d.launchMax, d.ty - this.reach + 0.15));
        const up = p < 1 ? Math.sin(p * Math.PI * 0.5) : Math.max(0, 1 - (t - d.duration) * 2.2);
        root.position.y = rise * up;
        root.position.x = lerp(d.x0, d.tx * 0.85 + d.x0 * 0.15, ease(p));
        root.rotation.z = -Math.atan2(d.tx - d.x0, 2) * 0.6 * ease(p);
        if (t <= 0) this.blendPose(POSES.set, Math.min(1, dt * 10));
        else this.blendPose(up > 0.05 ? POSES.jump : POSES.ready, Math.min(1, dt * 12));
        break;
      }
      case 'block':
      case 'smother': {
        const t = Math.max(0, this.t - d.delay);
        const p = Math.min(1, t / d.duration);
        root.position.x = lerp(d.x0, d.tx, ease(p) * 0.9);
        root.position.y = 0;
        root.rotation.z = 0;
        if (t <= 0) this.blendPose(POSES.set, Math.min(1, dt * 10));
        else this.blendPose(this.mode === 'block' ? POSES.block : POSES.smother, Math.min(1, dt * 14));
        break;
      }
      case 'celebrate': {
        this.blendPose(POSES.celebrate, Math.min(1, dt * 6));
        root.position.y = Math.abs(Math.sin(this.t * 6)) * 0.12 * Math.max(0, 1 - this.t / 2);
        root.rotation.z *= 0.9;
        break;
      }
      case 'dejected': {
        this.blendPose(POSES.dejected, Math.min(1, dt * 4));
        root.position.y = 0;
        root.rotation.z *= 0.9;
        break;
      }
      default:
        break;
    }
    this.applyPose(this.pose);
    if (this.mode === 'ready' || this.mode === 'set' || this.mode === 'celebrate' || this.mode === 'dejected'
      || this.mode === 'block' || this.mode === 'smother') this.groundFeet();
    this.updateColliders();
  }

  /** Keep the lower boot on the grass when standing (poses only approximate leg length). */
  groundFeet() {
    this.root.updateMatrixWorld(true);
    const v = new THREE.Vector3();
    let minY = Infinity;
    for (const n of ['ankN', 'ankP']) {
      this.joints[n].localToWorld(v.set(0, -0.075, 0.05));
      minY = Math.min(minY, v.y);
    }
    const fix = this.root.position.y - minY; // feet rest at the root height (which carries any hop)
    if (Math.abs(fix) > 1e-4) {
      this.joints.hips.position.y += fix;
      this.pose.hipsY += fix * 0.5; // let the stored pose converge too
    }
  }

  updateColliders() {
    this.root.updateMatrixWorld(true);
    if (this.world.length !== this.colliders.length) {
      this.world = this.colliders.map((c) => ({ pos: new THREE.Vector3(), r: c.r, kind: c.kind }));
    }
    for (let i = 0; i < this.colliders.length; i++) this.colliders[i].obj.getWorldPosition(this.world[i].pos);
  }

  /** Flat snapshot of the rig, used to record and play back replays. */
  snapshot(out = []) {
    out.length = 0;
    out.push(this.root.position.x, this.root.position.y, this.root.position.z, this.root.rotation.z, this.joints.hips.position.y);
    for (const j of JOINTS) { const r = this.joints[j].rotation; out.push(r.x, r.y, r.z); }
    return out;
  }

  restore(s) {
    this.root.position.set(s[0], s[1], s[2]);
    this.root.rotation.set(0, 0, s[3]);
    this.joints.hips.position.y = s[4];
    let i = 5;
    for (const j of JOINTS) { this.joints[j].rotation.set(s[i], s[i + 1], s[i + 2]); i += 3; }
    this.root.updateMatrixWorld(true);
  }

  dispose() {
    this.scene.remove(this.root);
    this.materials.shirt.map?.dispose();
    for (const d of this.disposables) d.dispose();
  }
}
